import useBundleTranslation from 'i18n';
import { UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import React, { useState } from 'react';
import MarkdownIt from 'markdown-it';
import { useQuery } from '@tanstack/react-query';
import { conciergeAPI } from 'api/concierge';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { Box, Tab, Tabs } from '@mui/material';
import parse from 'html-react-parser';
import katex from 'katex';
import { escapeLatexSpecial, parseMarkdownTable } from 'app/concierge/tools';
import 'katex/dist/katex.min.css';
import defaultStyles from 'app/concierge/panel/active-chat/message/styles';
import { IMessagePart } from 'app/concierge/types';
import JsonTable from 'app/concierge/panel/active-chat/message/parts/JsonTable';
import HtmlPart from 'app/concierge/panel/active-chat/message/parts/HtmlPart';
import { escapeHtml } from 'tools/tools';
import ReactJsonView from '@microlink/react-json-view';

const md = MarkdownIt();
const LATEX_IN_MESSAGE_REGEX = /\\\[\s*(.*?)\s*\\]/gm;
const INLINE_LATEX_IN_MESSAGE_REGEX = /\\\(\s*(.*?)\s*\\\)/gm;
const MD_LINK_REGEX = /^(.*?):(.*)?\[Link\]\(([^)]+)\)(.*)$/gm;
const MD_LINK_REPLACER = parse(`<a href="$3" target="_blank">$1</a>:<span>$2</span><br /><br />`) as string;
const MD_NOT_FULL_LINK_REGEX = /(\[([^\]]+)]\(((http)|\/|\.)[^)]+(?!=\)))$/i;
const MD_NOT_FULL_LINK_REPLACER = parse(`$2`) as string;

function SummarizedTab({ sessionUid }: { sessionUid: string }) {
    const { t } = useBundleTranslation('main');
    const queryKey = `concierge_llm_log_summary_${sessionUid}`;

    const { data: summarized } = useQuery<any, Error>({
        queryKey: [queryKey],
        queryFn: () => {
            return conciergeAPI.getLLMLogSummary(sessionUid);
        },
    });
    if (null == summarized) return <div>{t('loading___')}</div>;
    const renderedHtml = md.render(summarized);
    let styles = defaultStyles;
    return (
        <Box sx={{ overflow: 'auto' }}>
            <Box
                key={`html-summarized`}
                sx={styles.dialogItemText.messageText}
                dangerouslySetInnerHTML={{
                    __html: renderedHtml,
                }}
            />
        </Box>
    );
}

export default function LLMLogPopup({ sessionUid }: { sessionUid: string }) {
    const { t } = useBundleTranslation('app/concierge/widget');

    const [showPopup, setShowPopup] = useState(true);
    const [currentTab, setCurrentTab] = useState('detail');

    const queryKey = `concierge_llm_log_${sessionUid}`;
    let styles = defaultStyles;
    if (null === sessionUid) return null;

    const { data: llmLog } = useQuery<any, Error>({
        queryKey: [queryKey],
        queryFn: () => {
            return conciergeAPI.getLLMLog(sessionUid);
        },
    });

    if (null == llmLog && !llmLog?.length) return null;

    const popupSettings: PopupSettings = {
        title: t('llm_log'),
        needTranslation: false,
        maxWidth: 'popupXl',
        noOk: true,
        textCancel: t('done'),
    };

    const renderLogItem = (logItem: any) => {
        const drawMessage = (parts: IMessagePart[], type: 'response' | 'request') => {
            let allPartsAreMarkdown = true;
            let markdownMessage = '';
            let index = logItem.id;
            let allowJS = false;

            parts.forEach((part, index) => {
                if (part.type !== 'markdown') {
                    allPartsAreMarkdown = false;
                    return;
                } else {
                    markdownMessage += part.message;
                }
            });

            if (allPartsAreMarkdown) {
                const hasLatex = LATEX_IN_MESSAGE_REGEX.exec(markdownMessage) !== null;
                // Reset regex position
                LATEX_IN_MESSAGE_REGEX.lastIndex = 0;

                if (hasLatex) {
                    const latexParts = markdownMessage.split(LATEX_IN_MESSAGE_REGEX);
                    const parts = [];

                    for (let j = 0; j < latexParts.length; j++) {
                        if (j % 2 === 0) {
                            const inlineFormula: string[] = [];

                            parts.push(
                                <Box
                                    sx={styles.dialogItemText.messageText}
                                    key={index}
                                    dangerouslySetInnerHTML={{
                                        __html: md
                                            .render(
                                                latexParts[j]
                                                    .split(INLINE_LATEX_IN_MESSAGE_REGEX)
                                                    .map((part, index) => {
                                                        if (index % 2 === 0) {
                                                            return part;
                                                        } else {
                                                            inlineFormula.push(
                                                                `<span class="formula-inline">
                                                            ${katex.renderToString(escapeLatexSpecial(part.trim()), {
                                                                throwOnError: false,
                                                                displayMode: false,
                                                                maxSize: 50,
                                                            })}
                                                        </span>`,
                                                            );

                                                            return `%INLINE_FORMULA_${inlineFormula.length - 1}%`;
                                                        }
                                                    })
                                                    .join('')
                                                    .trim()
                                                    .replace(MD_LINK_REGEX, MD_LINK_REPLACER)
                                                    .replace(MD_NOT_FULL_LINK_REGEX, MD_NOT_FULL_LINK_REPLACER),
                                            )
                                            .replace(/%INLINE_FORMULA_(\d+)%/g, (_, index) => inlineFormula[index]),
                                    }}
                                />,
                            );
                        } else {
                            parts.push(
                                <Box
                                    sx={styles.formulaContainer}
                                    key={index}
                                    dangerouslySetInnerHTML={{
                                        __html: katex.renderToString(latexParts[j].trim(), {
                                            throwOnError: false,
                                            displayMode: false,
                                            output: 'html',
                                        }),
                                    }}
                                />,
                            );
                        }
                    }

                    return <Box sx={styles.dialogItemText.messageText}>{parts}</Box>;
                }

                const renderedHtml = md.render(markdownMessage);
                const parsedTables = parseMarkdownTable(renderedHtml);

                if (parsedTables.length > 0) {
                    const parts: React.ReactNode[] = [];

                    parsedTables.forEach((tableData, idx) => {
                        if (tableData.htmlBefore) {
                            parts.push(
                                <Box
                                    key={`html-${idx}`}
                                    sx={styles.dialogItemText.messageText}
                                    dangerouslySetInnerHTML={{
                                        __html: tableData.htmlBefore,
                                    }}
                                />,
                            );
                        }

                        parts.push(
                            <JsonTable
                                key={`table-${idx}`}
                                tableData={tableData}
                                messageId={`msg-${index}-table-${idx}`}
                                conciergeResponse={null}
                            />,
                        );
                    });

                    const lastTable = parsedTables[parsedTables.length - 1];
                    if (lastTable.htmlAfter) {
                        parts.push(
                            <Box
                                key="html-after"
                                sx={styles.dialogItemText.messageText}
                                dangerouslySetInnerHTML={{
                                    __html: lastTable.htmlAfter,
                                }}
                            />,
                        );
                    }

                    return <Box sx={styles.dialogItemText.messageText}>{parts}</Box>;
                }

                return (
                    <Box
                        sx={styles.dialogItemText.messageText}
                        key={index}
                        dangerouslySetInnerHTML={{
                            __html: renderedHtml,
                        }}
                    />
                );
            } else {
                markdownMessage = '';
            }

            return (
                <Box sx={styles.dialogItemText} justifyContent={'flex-start'} alignItems={'flex-start'}>
                    {parts.map((part, index) => {
                        if (part.type == 'html') {
                            if (type === 'response') {
                                if (allowJS) {
                                    return <HtmlPart key={index} htmlContent={part.message} />;
                                } else {
                                    return <Box key={index}>{parse(part.message)}</Box>;
                                }
                            } else {
                                const message = escapeHtml(part.message);
                                return <Box key={index}>{parse(message)}</Box>;
                            }
                        }

                        if (part.type == 'markdown') {
                            return <Box key={index}>{parse(md.render(part.message))}</Box>;
                        }

                        return <Box>{'not ready yet'}</Box>;
                    })}
                </Box>
            );
        };

        const renderMarkdown = (content: any) => {
            let tmpResponse = null;
            try {
                tmpResponse = JSON.parse(content);
            } catch (error) {}

            if (tmpResponse) return content;
            else {
                let part: IMessagePart = {
                    type: 'markdown',
                    message: content,
                    source: 'backend',
                };
                return drawMessage([part], 'response');
            }
        };

        let tmp = null;
        let firstLine: string | null = null;
        try {
            tmp = JSON.parse(logItem.message);
        } catch (error) {}

        if (null === tmp && null !== logItem.message && logItem.message.indexOf('\n') > 0) {
            const newLineIndex = logItem.message.indexOf('\n');
            let l = logItem.message.substring(newLineIndex + 1);

            if (l && l > '') {
                try {
                    tmp = JSON.parse(l);
                } catch (error) {}
            }
            if (tmp) firstLine = logItem.message.substring(0, newLineIndex);
        }

        if (tmp) {
            if (tmp.content && tmp.content > '') {
                return (
                    <div key={'log_item_wrapper_' + logItem.id}>
                        <div key={'log_item_' + logItem.id}>
                            <pre>{tmp.content}</pre>
                        </div>
                        <hr />
                        <br />
                    </div>
                );
            } else {
                let secondLine: any = null;
                if (tmp && tmp.choices && tmp.choices.length > 0) {
                    if (tmp.choices[tmp.choices.length - 1]?.message)
                        secondLine = tmp.choices[tmp.choices.length - 1]?.message;
                    else secondLine = tmp.choices[tmp.choices.length - 1];
                } else if (tmp && tmp.messages && tmp.messages.length > 0) {
                    secondLine = tmp.messages[tmp.messages.length - 1];
                }

                return (
                    <div key={'log_item_wrapper_' + logItem.id}>
                        <div key={'log_item_' + logItem.id}>{firstLine && <pre>{firstLine}</pre>}</div>
                        {secondLine && (
                            <div>
                                <div>{t('message.msg')}</div>
                                <div>
                                    <ReactJsonView
                                        src={secondLine}
                                        collapsed={false}
                                        displayDataTypes={false}
                                        collapseStringsAfterLength={50}
                                    />
                                </div>
                                <div>{t('message.entire_request')}</div>
                            </div>
                        )}
                        <div>
                            <ReactJsonView
                                src={tmp}
                                collapsed={secondLine !== null}
                                displayDataTypes={false}
                                collapseStringsAfterLength={50}
                            />
                        </div>
                        <hr />
                        <br />
                    </div>
                );
            }
        }

        return (
            <div key={'log_item_wrapper_' + logItem.id}>
                <div key={'log_item_' + logItem.id}>
                    {'response' == logItem.message_type ? (
                        renderMarkdown(logItem.message)
                    ) : (
                        <pre>{logItem.message}</pre>
                    )}
                </div>
                <br />
                <div key={'log_item_log_time' + logItem.id}>
                    {t('log_time')}: {logItem.log_time}
                </div>
                <hr />
                <br />
            </div>
        );
    };

    return (
        <Popup onHide={() => setShowPopup(false)} settings={popupSettings} open={showPopup}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'left',
                    width: '100%',
                    height: '600px',
                    overflowX: 'auto',
                }}
            >
                <Tabs
                    variant="scrollable"
                    value={currentTab}
                    sx={{
                        height: '45px',
                    }}
                    onChange={(_, value) => {
                        setCurrentTab(value);
                    }}
                >
                    <Tab label={t(`detail`)} key={'detail'} value={'detail'} />
                    <Tab label={t(`summarized`)} key={'summarized'} value={'summarized'} />
                </Tabs>

                {'detail' == currentTab && llmLog.map((logItem: any, k: number) => renderLogItem(logItem))}
                {'summarized' == currentTab && <SummarizedTab sessionUid={sessionUid} />}
            </div>
        </Popup>
    );
}
